import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Save, Mail, UserCheck, UserX, Info, Code } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface EmailTemplate {
  id: string;
  template_type: string;
  subject: string;
  body_html: string;
  placeholders: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const templateLabels: Record<string, { label: string; icon: React.ReactNode; description: string }> = {
  interview_invitation: {
    label: "Einladung zum Gespräch",
    icon: <Mail className="h-4 w-4" />,
    description: "E-Mail für die Einladung von Bewerbern zu einem Vorstellungsgespräch"
  },
  job_offer: {
    label: "Stellenangebot",
    icon: <UserCheck className="h-4 w-4" />,
    description: "E-Mail mit einem Jobangebot an den Bewerber"
  },
  rejection: {
    label: "Absage",
    icon: <UserX className="h-4 w-4" />,
    description: "Freundliche Absage-E-Mail mit DSGVO-Hinweis"
  }
};

const placeholderDescriptions: Record<string, string> = {
  applicant_name: "Vollständiger Name des Bewerbers",
  company_name: "Name des Unternehmens",
  position: "Beworbene Stelle",
  interview_date: "Datum des Vorstellungsgesprächs",
  interview_time: "Uhrzeit des Vorstellungsgesprächs",
  interview_location: "Ort des Vorstellungsgesprächs",
  custom_message: "Individuelle Nachricht",
  response_link: "Link zur Terminbestätigung",
  salary_amount: "Gehaltsbetrag",
  salary_type: "Gehaltsart (brutto/netto)",
  weekly_hours: "Wöchentliche Arbeitsstunden",
  vacation_days: "Urlaubstage pro Jahr",
  start_date: "Geplantes Startdatum",
  additional_benefits: "Zusätzliche Benefits",
  rejection_reason: "Grund der Absage (optional)"
};

export const EmailTemplatesSettings = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("interview_invitation");
  const [showPlaceholders, setShowPlaceholders] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("applicant_email_templates")
        .select("*")
        .order("template_type");

      if (error) throw error;
      
      const typedData = (data || []).map(item => ({
        ...item,
        placeholders: Array.isArray(item.placeholders) 
          ? item.placeholders as string[]
          : []
      }));
      
      setTemplates(typedData);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Fehler beim Laden der Vorlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (template: EmailTemplate) => {
    setSaving(template.id);
    try {
      const { error } = await supabase
        .from("applicant_email_templates")
        .update({
          subject: template.subject,
          body_html: template.body_html,
          is_active: template.is_active
        })
        .eq("id", template.id);

      if (error) throw error;
      toast.success("Vorlage gespeichert");
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(null);
    }
  };

  const updateTemplate = (id: string, field: keyof EmailTemplate, value: string | boolean) => {
    setTemplates(prev => 
      prev.map(t => t.id === id ? { ...t, [field]: value } : t)
    );
  };

  const insertPlaceholder = (templateId: string, placeholder: string) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    const placeholderText = `{{${placeholder}}}`;
    const textarea = document.getElementById(`body-${templateId}`) as HTMLTextAreaElement;
    
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = 
        template.body_html.substring(0, start) + 
        placeholderText + 
        template.body_html.substring(end);
      
      updateTemplate(templateId, "body_html", newValue);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholderText.length, start + placeholderText.length);
      }, 0);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            E-Mail-Vorlagen
          </CardTitle>
          <CardDescription>
            Bearbeiten Sie die E-Mail-Vorlagen für die Bewerberkommunikation. Verwenden Sie Platzhalter in doppelten geschweiften Klammern.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 h-auto gap-1 mb-6">
              {templates.map((template) => {
                const meta = templateLabels[template.template_type];
                return (
                  <TabsTrigger 
                    key={template.template_type} 
                    value={template.template_type}
                    className="flex items-center gap-2 text-xs sm:text-sm py-2"
                  >
                    {meta?.icon}
                    <span className="hidden sm:inline">{meta?.label || template.template_type}</span>
                    <span className="sm:hidden">{meta?.label?.split(" ")[0] || template.template_type}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {templates.map((template) => {
              const meta = templateLabels[template.template_type];
              return (
                <TabsContent key={template.template_type} value={template.template_type}>
                  <div className="space-y-6">
                    {meta && (
                      <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                        <Info className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">{meta.description}</p>
                      </div>
                    )}

                    <Collapsible 
                      open={showPlaceholders[template.id]}
                      onOpenChange={(open) => setShowPlaceholders(prev => ({ ...prev, [template.id]: open }))}
                    >
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-between">
                          <span className="flex items-center gap-2">
                            <Code className="h-4 w-4" />
                            Verfügbare Platzhalter
                          </span>
                          <Badge variant="secondary">{template.placeholders.length}</Badge>
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-muted/30 rounded-lg">
                          <TooltipProvider>
                            {template.placeholders.map((placeholder) => (
                              <Tooltip key={placeholder}>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="justify-start font-mono text-xs h-auto py-1.5"
                                    onClick={() => insertPlaceholder(template.id, placeholder)}
                                  >
                                    <Code className="h-3 w-3 mr-2 text-primary" />
                                    {`{{${placeholder}}}`}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{placeholderDescriptions[placeholder] || placeholder}</p>
                                  <p className="text-xs text-muted-foreground mt-1">Klicken zum Einfügen</p>
                                </TooltipContent>
                              </Tooltip>
                            ))}
                          </TooltipProvider>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <div className="space-y-2">
                      <Label htmlFor={`subject-${template.id}`}>Betreff</Label>
                      <Input
                        id={`subject-${template.id}`}
                        value={template.subject}
                        onChange={(e) => updateTemplate(template.id, "subject", e.target.value)}
                        placeholder="E-Mail Betreff"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`body-${template.id}`}>E-Mail-Text (HTML)</Label>
                      <Textarea
                        id={`body-${template.id}`}
                        value={template.body_html}
                        onChange={(e) => updateTemplate(template.id, "body_html", e.target.value)}
                        placeholder="E-Mail Inhalt..."
                        className="min-h-[300px] font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Sie können HTML-Tags wie &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt; verwenden.
                      </p>
                    </div>

                    <div className="flex justify-end">
                      <Button 
                        onClick={() => handleSave(template)}
                        disabled={saving === template.id}
                      >
                        {saving === template.id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Speichern
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailTemplatesSettings;
